import instantsearch from 'instantsearch.js';
import { configure, searchBox, hits, clearRefinements } from 'instantsearch.js/es/widgets';
import searchClient from '@pratt/js/components/algolia/search-client';
import { customPagination } from '@pratt/js/components/algolia/pagination';
import { refinementListWithPanel } from '@pratt/js/components/algolia/panel';
import { refinementListTemplate } from '@pratt/js/components/algolia/templates/refinement-list';
import { createMiddleware } from '@pratt/js/components/algolia/insights';
import { createSearchRouting } from '@pratt/js/components/algolia/search-router';

class PrattfolioStorySearch {
  constructor(rootEl) {
    this.rootEl = rootEl;
    if (!this.rootEl) {
      throw new Error('Root prattfolio story search element not found.');
    }

    this.hitsPerPage = 24;
    this.search = instantsearch({
      indexName: this.rootEl.dataset['algoliaIndex'],
      searchClient: searchClient,
      routing: createSearchRouting(this.rootEl.dataset['algoliaIndex']),
      insights: true,
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    try {
      const insightsMiddleware = createMiddleware('Prattfolio Story');
      this.search.use(insightsMiddleware);
    } catch (error) {
      console.error('Failed to initialize Algolia Insights middleware:', error);
    }

    this.init();
    this.search.start();
  }

  init() {
    // Function to extract the numeric value after "publish_date: "
    function extractPublishDate(label) {
      const match = label.match(/publish_date:\s*(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    }
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter results by 'type' facet
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: `type:"prattfolio-story"`,
      }),

      searchBox({
        container: '.js-prattfolio-story-search-input',
        placeholder: 'Search Prattfolio Stories',
        showSubmit: false,
      }),

      clearRefinements({
        container: '.js-prattfolio-story-search-clear',
        templates: {
          resetLabel: 'Reset',
        },
      }),

      refinementListWithPanel('School')({
        container: '.js-prattfolio-story-search-schools-input',
        attribute: 'schools',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Program')({
        container: '.js-prattfolio-story-search-programs-input',
        attribute: 'programs',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Minor')({
        container: '.js-prattfolio-story-search-minors-input',
        attribute: 'minors',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Prattfolio Department')({
        container: '.js-prattfolio-story-search-prattfolio-departments-input',
        attribute: 'prattfolio_departments',
        operator: 'and',
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
        showMore: true,
      }),

      refinementListWithPanel('Issue')({
        container: '.js-prattfolio-story-search-issues-input',
        attribute: 'issue',
        operator: 'and',
        templates: {
          item: refinementListTemplate,
        },
        transformItems: items => {
          // Sort items by the numeric value after "publish_date: "
          items.sort((a, b) => {
            const dateA = extractPublishDate(a.label);
            const dateB = extractPublishDate(b.label);

            if (dateA && dateB) {
              return dateB - dateA;
            }

            return 0;
          });

          // Modify items to display only the string before "|"
          return items.map(item => {
            const label = item.label.split(' | ')[0]; // Extract the string before "|"
            return {
              ...item,
              highlighted: label,
            };
          });
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: '.js-prattfolio-story-search-hits',
        templates: {
          item(hit) {
            return `
              <div class="article-hit article-hit--prattfolio-story">
                <div class="article-hit-inner">
                  <div class="article-hit__titles">
                    <h3 class="article-hit__title">
                      <a href="${hit.url}" class="article-hit__title-link">
                        <span class="article-hit__title-text">${hit.title}</span>
                      </a>
                    </h3>
                  </div>

                  ${hit.excerpt ? `
                    <div class="article-hit__content">
                      ${hit.excerpt.replace('[&hellip;]', '&hellip;')}
                    </div>
                  ` : ''}
                  ${hit.thumbnail ? `
                    <div class="article-hit__image">
                      <img src="${hit.thumbnail}" alt="${hit.title}" />
                    </div>
                  ` : ''}
                </div>
              </div>
            `;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      customPagination({
        container: document.querySelector('.js-prattfolio-story-search-pagination'),
      }),
    ]);
  }
}

export default PrattfolioStorySearch;
